import { useState } from 'react'

import AddIcon from '@mui/icons-material/Add'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'

import StyledTable from '@components/StyledTable'
import { ToggleSetButton } from '@components/ToggleSetButton'

import type { AuthenticatedUser } from '../../../auth/types/AuthenticatedUser'
import { StyledIconButton } from '../../../theme/components/StyledIconButton'
import type { AssignedBijlage as AssignedBijlageType } from '../types/AssignedBijlage'
import type { Bijlage } from '../types/Bijlage'

import { AssignedBijlage } from './AssignedBijlage'

const headerCells = [
  { id: 'status', label: '', fixed: 10 },
  { id: 'documentsoort', label: 'Documentsoort', sort: false, fixed: 500 },
  { id: 'assignedTo', label: 'Toegewezen aan', sort: false, fixed: 250 },
  { id: 'dueDate', label: 'Opleveren voor', sort: false, fixed: 200 },
  { id: 'created.date', label: 'Aangemaakt', fixed: 50 },
  { id: 'created.organisatie', label: 'Aangemaakt door', fixed: 150 },
  { id: 'changed', label: 'Opgeleverd', sort: false, fixed: 150 },
  { id: 'opties', label: 'Opties', sort: false, fixed: 180 },
]

const notCancelledFilter = (x) => x.status !== 'cancelled'
const cancelledFilter = (x) => x.status === 'cancelled'

interface Props {
  assignedBijlagen: AssignedBijlageType[]
  bijlagen: Bijlage[]
  user: AuthenticatedUser | Record<string, any>
  onOpen: (bijlage: Bijlage) => void
  onUpload: (bijlage: AssignedBijlageType) => void
  onCreate: () => void
  onCancel: (bijlage: Bijlage | AssignedBijlageType) => void
  onOpleveren: (bijlage: AssignedBijlageType) => void
  onReview: (bijlage: AssignedBijlageType) => void
  onEdit: (bijlage: AssignedBijlageType) => void
  showCreateAssignedBijlage: boolean
  showCancelAssignedBijlage: boolean
  isVerantwoordelijke: boolean
}

export const AssignedBijlagen = ({
  assignedBijlagen,
  bijlagen,
  user,
  onOpen,
  onUpload,
  onCreate,
  onCancel,
  showCreateAssignedBijlage,
  showCancelAssignedBijlage,
  onOpleveren,
  onReview,
  onEdit,
  isVerantwoordelijke,
}: Props) => {
  const [showCancelled, setShowCancelled] = useState(false)

  return (
    <>
      <Toolbar
        sx={{
          justifyContent: 'flex-end',
          backgroundColor: '#F7F7F7',
          borderBottom: '1px solid #E0E0E0',
        }}
      >
        <Box sx={{ p: 1, textAlign: 'right' }}>
          {showCreateAssignedBijlage && (
            <StyledIconButton
              title="Bijlage toevoegen"
              icon={<AddIcon />}
              onClick={onCreate}
            />
          )}

          <ToggleSetButton
            active={showCancelled}
            onClick={() => setShowCancelled(!showCancelled)}
            toggledSetSize={
              [...bijlagen, ...assignedBijlagen].filter(cancelledFilter).length
            }
            type="bijlagen"
          />
        </Box>
      </Toolbar>
      <StyledTable variant="details" headerCells={headerCells} {...({} as any)}>
        {assignedBijlagen &&
          assignedBijlagen
            .filter(showCancelled ? () => true : notCancelledFilter)
            .map((assignedBijlage) => {
              return (
                <AssignedBijlage
                  key={assignedBijlage._id}
                  assignedBijlage={assignedBijlage}
                  bijlagen={bijlagen
                    .filter(
                      (bijlage) => bijlage.parentId === assignedBijlage._id,
                    )
                    .filter(showCancelled ? () => true : notCancelledFilter)}
                  user={user}
                  onOpen={onOpen}
                  onUpload={onUpload}
                  onCancel={onCancel}
                  onOpleveren={onOpleveren}
                  onReview={onReview}
                  onEdit={onEdit}
                  showCancelAssignedBijlage={showCancelAssignedBijlage}
                  isVerantwoordelijke={isVerantwoordelijke}
                />
              )
            })}
      </StyledTable>
    </>
  )
}
